import { t, tc } from '../i18n/i18n';
/**
 * What the thing in front of you does, said where a player can see it
 * (2026-09-07).
 *
 * User: *"I completed the entire dungeon, reached the exit, held E, and the
 * red exit bar simply disappeared, but nothing happened."*
 *
 * Part of why nothing on screen explained that: **there was no prompt.** Every
 * contextual line this game had — "hold E to revive", "press E to search the
 * body", "cranking: 68%" — was passed to `DebugHud.updateStats` as its `extra`
 * argument, which renders inside the collapsible block that is `hidden` until
 * somebody presses F1. Those lines were written as player-facing copy, their
 * own comments say so ("without it a body on the floor is scenery"), and no
 * player has ever seen one.
 *
 * So this is that panel, always visible, sitting just above the action bar
 * where the eye already goes for what the buttons do. Styled from
 * `hud-theme`'s `frame-pewter` like the mouse-look prompt, because they are
 * the same kind of object: a sentence the world owes you.
 *
 * Multiple lines because the gate asks a real question — climb out with the
 * bag, or stake it on the floor below — and cramming a decision that ends or
 * risks an entire run into one sentence is how it stops being read.
 */
/**
 * The line for a wall torch (2026-10-15, QA #15b): "the torch can be toggled
 * with E, but nothing says so". Every sconce in the dungeon is a server-side
 * brazier the E key lights or snuffs (`findNearestInteractable` already
 * offered it), and it was the one interactable with no prompt at all.
 */
export function brazierPromptLine(state: string): string {
  return state === 'LIT'
    ? t('{press} to snuff the torch', { press: pressKey() })
    : t('{press} to light the torch', { press: pressKey() });
}

/**
 * The key verbs every prompt line starts with (2026-10-16, i18n). A line is
 * one key with the verb as a placeholder — `t('{press} to open the chest',
 * { press: pressKey() })` — so the touch rewrite below can swap the
 * translated verb for the translated touch verb in any language, not only
 * in English.
 */
export function pressKey(key = 'E'): string {
  return t('press {key}', { key });
}

export function holdKey(key = 'E'): string {
  return t('hold {key}', { key });
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * The same instructions for thumbs (2026-10-15). A phone has no E key — the
 * touch cluster's "Use" plate is what sends the interact — so a prompt that
 * says "press E" there names a control the player does not have. Rewritten at
 * the panel rather than at every call site, so a new prompt line cannot forget
 * it. Only the E verb has a touch equivalent; other keys are left as written.
 */
export function promptLinesForInput(lines: readonly string[], touch: boolean): string[] {
  if (!touch) return [...lines];
  // The plate's own label, so the prompt names the button the thumb can see.
  const use = tc('touch control', 'Use');
  const press = new RegExp(`${escapeRegExp(pressKey())}(?![\\p{L}\\p{N}])`, 'gu');
  const hold = new RegExp(`${escapeRegExp(holdKey())}(?![\\p{L}\\p{N}])`, 'gu');
  const tap = t('tap {key}', { key: use });
  const holdUse = t('hold {key}', { key: use });
  return lines.map((line) => line.replace(press, () => tap).replace(hold, () => holdUse));
}

export class InteractPrompt {
  private readonly root: HTMLDivElement;
  private current = '';

  constructor(container: HTMLElement) {
    this.root = document.createElement('div');
    this.root.dataset.hud = 'interact-prompt';
    this.root.className = [
      // `w-max` (QA 2026-10-16): a fixed box at `left: 50%` shrink-wraps to
      // the half screen right of its own left edge, so on a 320px phone a
      // five-word line wrapped into a 160px tower. The cap is the max-width.
      'fixed', 'left-1/2', '-translate-x-1/2', 'bottom-[13%]', 'z-[1140]', 'w-max', 'max-w-[min(32rem,calc(100vw-2rem))]',
      'pointer-events-none', 'select-none', 'hidden',
      'px-4', 'py-2', 'frame-pewter',
      'flex', 'flex-col', 'items-center', 'gap-0.5',
      'text-center', 'text-text', 'text-[clamp(0.8rem,1.7vw,0.95rem)]',
    ].join(' ');
    container.appendChild(this.root);
  }

  /**
   * Empty hides it. Rebuilt only when the text actually changes: this is
   * called every frame from the render loop, and reassigning DOM on a frame
   * where nothing changed is a layout thrash for no reason.
   */
  show(lines: readonly string[]): void {
    const next = lines.join('\n');
    if (next === this.current) return;
    this.current = next;

    this.root.replaceChildren();
    if (lines.length === 0) {
      this.root.classList.add('hidden');
      return;
    }
    for (const [index, text] of lines.entries()) {
      const el = document.createElement('div');
      // The first line is the one E does. Everything after it is an
      // alternative on another key, so it is dimmed — the reflex action should
      // be the one that reads loudest, and at a gate the reflex action is also
      // the one that keeps your loot.
      if (index > 0) el.className = 'text-text-dim text-[0.88em]';
      el.textContent = text;
      this.root.appendChild(el);
    }
    this.root.classList.remove('hidden');
  }

  hide(): void {
    this.show([]);
  }

  dispose(): void {
    this.root.remove();
  }
}
